/**
 * densityAltitudeCalculator.ts
 * Calcul de la densité-altitude pour CYRC (élévation 543 ft).
 * Formule simplifiée OACI.
 */

export const CYRC_FIELD_ELEVATION_FT = 543;

export interface DensityAltitudeResult {
  pressureAltitudeFt: number;
  densityAltitudeFt: number;
  isaTempC: number;
  tempDeviationC: number;
  level: 'normal' | 'surveiller' | 'prudence' | 'elevee';
  levelLabel: string;
  levelColor: string;
  levelBg: string;
  levelBorder: string;
}

/**
 * Calcule la densité-altitude à partir des conditions météo.
 * @param outsideTempC  Température extérieure en °C
 * @param pressureHpa   Pression QNH en hPa
 * @param fieldElevationFt  Élévation du terrain en ft (défaut CYRC = 543 ft)
 */
export function calculateDensityAltitude(
  outsideTempC: number,
  pressureHpa: number,
  fieldElevationFt: number = CYRC_FIELD_ELEVATION_FT
): DensityAltitudeResult {
  // Convertir hPa en inHg
  const altimeterInHg = pressureHpa * 0.0295299830714;

  // Altitude-pression
  const pressureAltitudeFt = fieldElevationFt + (29.92 - altimeterInHg) * 1000;

  // Température ISA à cette altitude-pression
  const isaTempC = 15 - (2 * pressureAltitudeFt) / 1000;

  // Densité-altitude
  const rawDensityAltitude = pressureAltitudeFt + 120 * (outsideTempC - isaTempC);

  // Arrondir à la centaine de pieds
  const densityAltitudeFt = Math.round(rawDensityAltitude / 100) * 100;

  const tempDeviationC = Math.round((outsideTempC - isaTempC) * 10) / 10;

  // Niveaux de risque
  let level: DensityAltitudeResult['level'];
  let levelLabel: string;
  let levelColor: string;
  let levelBg: string;
  let levelBorder: string;

  if (densityAltitudeFt < 1500) {
    level = 'normal';
    levelLabel = 'Normal';
    levelColor = '#16a34a';
    levelBg = '#f0fdf4';
    levelBorder = '#86efac';
  } else if (densityAltitudeFt < 2500) {
    level = 'surveiller';
    levelLabel = 'À surveiller';
    levelColor = '#d97706';
    levelBg = '#fffbeb';
    levelBorder = '#fde68a';
  } else if (densityAltitudeFt < 3500) {
    level = 'prudence';
    levelLabel = 'Prudence';
    levelColor = '#ea580c';
    levelBg = '#fff7ed';
    levelBorder = '#fdba74';
  } else {
    level = 'elevee';
    levelLabel = 'Élevée';
    levelColor = '#dc2626';
    levelBg = '#fef2f2';
    levelBorder = '#fca5a5';
  }

  return {
    pressureAltitudeFt: Math.round(pressureAltitudeFt),
    densityAltitudeFt,
    isaTempC: Math.round(isaTempC * 10) / 10,
    tempDeviationC,
    level,
    levelLabel,
    levelColor,
    levelBg,
    levelBorder,
  };
}

/**
 * Retourne les alertes densité-altitude et conditions météo.
 */
export interface DensityAltitudeAlert {
  id: string;
  severity: 'info' | 'caution' | 'warning';
  message: string;
}

export function getDensityAltitudeAlerts(
  densityAltitudeFt: number,
  outsideTempC: number,
  humidity: number,
  tailwindKt: number
): DensityAltitudeAlert[] {
  const alerts: DensityAltitudeAlert[] = [];

  if (densityAltitudeFt > 3500) {
    alerts.push({
      id: 'da-very-high',
      severity: 'warning',
      message: 'Densité-altitude très élevée — calcul performance requis',
    });
  } else if (densityAltitudeFt > 2500) {
    alerts.push({
      id: 'da-high',
      severity: 'caution',
      message: 'Densité-altitude élevée — vérifier distance de décollage',
    });
  }

  if (outsideTempC > 25 && humidity > 70) {
    alerts.push({
      id: 'hot-humid',
      severity: 'caution',
      message: 'Air chaud et humide — performances réduites possibles',
    });
  }

  if (tailwindKt > 5) {
    alerts.push({
      id: 'tailwind',
      severity: 'warning',
      message: 'Vent arrière — distance de décollage augmentée',
    });
  }

  return alerts;
}
